export type ProductPrefix = 'syn' | 'pds' | 'vs' | 'am' | 'pm' | 'dm';

export type TrademarkType = 'registered' | 'trademark';

export type ProductMeta = {
  /** Alphanumeric product code, e.g. "DS-01". */
  code: string;
  /** Human-readable product name, e.g. "Daily Synbiotic". */
  name: string;
  /** Whether the code carries a ® (registered) or ™ (trademark) symbol. */
  trademark: TrademarkType;
};

/**
 * Resolve product metadata from a SKU.
 *
 * The SKU is normalized by splitting on "-" and taking the first segment, so
 * suffixes like "-rf" (refill), "-wk" (welcome kit), or "-duo" all resolve to
 * the same product.
 *
 * @example
 * getProductMeta('syn-rf')
 * // => { code: 'DS-01', name: 'Daily Synbiotic', trademark: 'registered' }
 *
 * @returns The matching {@link ProductMeta}, or `null` for an unknown prefix.
 */
export const getProductMeta = (sku: string): ProductMeta => {
  const prefix = sku.split('-')[0]?.toLowerCase();

  switch (prefix) {
    case 'pds':
      return {
        code: 'PDS-08',
        name: 'Pediatric Synbiotic',
        trademark: 'registered',
      };
    case 'vs':
      return {
        code: 'VS-01',
        name: 'Vaginal Synbiotic',
        trademark: 'trademark',
      };
    case 'am':
      return { code: 'AM-02', name: 'Energy + Focus', trademark: 'registered' };
    case 'pm':
      return {
        code: 'PM-02',
        name: 'Sleep + Restore',
        trademark: 'registered',
      };
    case 'dm':
      return {
        code: 'DM-02',
        name: 'Daily Multivitamin',
        trademark: 'registered',
      };
    case 'syn':
    default:
      return {
        code: 'DS-01',
        name: 'Daily Synbiotic',
        trademark: 'registered',
      };
  }
};
